import { useState } from 'react';
import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import type { SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { invitationDeliveryLabelKeys, type CreatedInvitationNotice } from './invitations';

export interface InvitationLinkNoticeProps {
  locale: SupportedLocale;
  /** The one-time link (the server keeps only a hash, so this is the only chance to copy it). */
  notice: CreatedInvitationNotice;
  /** Extra lines about the invitation, such as the roles it offers. */
  children?: React.ReactNode;
}

/**
 * Says whether the invitation was e-mailed and shows the link with a copy button, so the inviter can
 * still hand it over when the e-mail was not sent.
 */
export function InvitationLinkNotice({
  locale,
  notice,
  children,
}: InvitationLinkNoticeProps): React.JSX.Element {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(notice.link);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }

  return (
    <Alert severity={notice.emailDelivery === 'sent' ? 'success' : 'warning'} sx={{ mb: 2 }}>
      <Typography gutterBottom role="status">
        {translate(locale, invitationDeliveryLabelKeys[notice.emailDelivery]).replace(
          '{email}',
          notice.recipientEmail,
        )}
      </Typography>
      <Typography gutterBottom>{translate(locale, 'invitations.created')}</Typography>
      {children}
      <TextField
        fullWidth
        size="small"
        label={translate(locale, 'invitations.link')}
        value={notice.link}
        slotProps={{ input: { readOnly: true } }}
        onFocus={(event) => {
          event.target.select();
        }}
      />
      <Stack direction="row" spacing={2} sx={{ mt: 1, alignItems: 'center' }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            void copyLink();
          }}
        >
          {translate(locale, 'invitations.copy')}
        </Button>
        {copyState !== 'idle' && (
          <Typography component="span" role="status">
            {translate(
              locale,
              copyState === 'copied' ? 'invitations.copied' : 'invitations.copyFailed',
            )}
          </Typography>
        )}
      </Stack>
    </Alert>
  );
}
